import CommonAvatar from "@/components/Common/Avatar";
import { Input } from "@/components/ui/input";
import {
  useCreateProjectGroupsTasksMemos,
  useDeleteProjectGroupsTasksMemos,
  useUpdateProjectGroupsTasksMemos,
} from "@/libs/react-query/hooks/useTaskQuery";
import { differenceInMinutes, format, fromUnixTime, subHours } from "date-fns";
import { Memo } from "@/types/task";
import { isWithinInterval } from "date-fns";
import { isToday } from "date-fns";
import { ko } from "date-fns/locale";
import { useEffect, useRef } from "react";
import { useState } from "react";
import CommonImg from "@/components/Common/Img";
import EditMemo from "@/pages/Project/Detail/columns/components/EditMemo";
import PencilSvg from "@/assets/images/mdi-pencil.svg";
import { formatDate } from "@/libs/utils";
import { TaskStatus } from "@/consts/Task";

// 작업 상태 영문을 한글로 변환하는 매핑
const statusTextMap = {
  [TaskStatus.OPENED]: "요청됨",
  [TaskStatus.IN_PROGRESS]: "진행중",
  [TaskStatus.REVIEW]: "검토대기",
  [TaskStatus.CLOSED]: "완료됨",
  [TaskStatus.CANCELED]: "취소됨",
};

// 시스템 메시지 내용에서 영문 상태를 한글로 변환하는 함수
const convertStatusToKorean = (content: string): string => {
  let convertedContent = content;

  // 각 상태값을 한글로 변환
  Object.entries(statusTextMap).forEach(([englishStatus, koreanStatus]) => {
    const regex = new RegExp(`\\b${englishStatus}\\b`, "g");
    convertedContent = convertedContent.replace(regex, koreanStatus);
  });

  return convertedContent;
};

type Props = {
  memos: Memo[];
  taskId: string;
  userId: string;
  groupId: string;
};

export default function TaskUserMessage({
  memos = [],
  taskId,
  userId,
  groupId,
}: Props) {
  const [inputValue, setInputValue] = useState<string>("");
  const [editingMemoId, setEditingMemoId] = useState<string | null>(null);
  const [isComposing, setIsComposing] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { mutate: createMemo } = useCreateProjectGroupsTasksMemos({
    group_id: groupId,
    task_id: taskId,
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ block: "end" });
    }
  }, [memos]);

  const handleSendMessage = () => {
    if (inputValue.trim() === "") return;

    createMemo({ contents: inputValue });

    setInputValue("");
  };

  return (
    <div className="flex flex-col w-full h-full min-h-[360px]">
      <div className="flex flex-col justify-start flex-1 gap-2 overflow-y-auto">
        {memos.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center text-gray-B font-normal text-md">
            메시지가 없습니다
          </div>
        ) : (
          memos.map((memo, index) => {
            return (
              <div
                key={`memo-${index}-${memo.memo_id}`}
                className="flex flex-col gap-2"
              >
                {!memo.memo_id ? (
                  <SystemMessage memo={memo} />
                ) : (
                  <UserMessage
                    memo={memo}
                    isMe={userId === memo.created_by.user_id}
                    taskId={taskId}
                    groupId={groupId}
                    editingMemoId={editingMemoId}
                    setEditingMemoId={setEditingMemoId}
                  />
                )}
              </div>
            );
          })
        )}
        <div ref={scrollRef}></div>
      </div>
      <Input
        className="w-full py-4 h-[52px]"
        placeholder="메시지 남기기..."
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !isComposing) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
      />
    </div>
  );
}

const UserMessage = ({
  memo,
  isMe,
  taskId,
  groupId,
  editingMemoId,
  setEditingMemoId,
}: {
  memo: Memo;
  isMe: boolean;
  taskId: string;
  groupId: string;
  editingMemoId: string | null;
  setEditingMemoId: (id: string | null) => void;
}) => {
  const [inputValue, setInputValue] = useState(memo.contents);
  const [isEdit, setIsEdit] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: updateMemo } = useUpdateProjectGroupsTasksMemos({
    group_id: groupId,
    task_id: taskId,
    memo_id: memo.memo_id || "",
  });
  const { mutate: deleteMemo } = useDeleteProjectGroupsTasksMemos({
    group_id: groupId,
    task_id: taskId,
    memo_id: memo.memo_id || "",
  });

  const handleUpdateMemo = () => {
    updateMemo({ contents: inputValue });
    setIsEdit(false);
    setEditingMemoId(null);
  };

  const handleDeleteMemo = () => {
    deleteMemo();

    if (isEdit) {
      setIsEdit(false);
      setEditingMemoId(null);
    }
  };

  const onClickEdit = () => {
    setEditingMemoId(memo.memo_id);
    setIsEdit(true);
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (editingMemoId !== memo.memo_id) {
      setIsEdit(false);
    }
  }, [editingMemoId, memo.memo_id]);

  const getTime = (time: number) => {
    const today = new Date();
    const memoDate = fromUnixTime(time);

    if (isWithinInterval(memoDate, { start: subHours(today, 1), end: today })) {
      const diff = differenceInMinutes(today, memoDate);
      return `${diff}분 전`;
    } else if (isToday(memoDate)) {
      return format(memoDate, "오늘 a h:mm", { locale: ko });
    } else {
      return format(memoDate, "yyyy-MM-dd");
    }
  };

  return (
    <div className="flex items-center justify-between min-h-8">
      <div className="flex items-center space-x-2 flex-1">
        <div className="flex items-center gap-2">
          <CommonAvatar
            img_url={memo.created_by.img_url}
            name={memo.created_by.user_name}
          />
          <span className="w-3 text-dark-red text-xs font-bold">
            {isMe ? "나" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-A font-normal flex-1">
          {isEdit ? (
            <EditMemo
              inputRef={inputRef}
              inputValue={inputValue}
              setInputValue={setInputValue}
              setIsEdit={setIsEdit}
              handleUpdateMemo={handleUpdateMemo}
              handleDeleteMemo={handleDeleteMemo}
            />
          ) : (
            <>
              <span>{memo.contents}</span>
              {isMe && (
                <CommonImg
                  src={PencilSvg}
                  alt="Edit"
                  className="size-[10px] shrink-0 cursor-pointer"
                  onClick={onClickEdit}
                />
              )}
            </>
          )}
        </div>
      </div>
      <div className="text-[10px] text-gray-B font-semibold min-w-24 text-right">
        {getTime(memo.created_at)}
      </div>
    </div>
  );
};

const SystemMessage = ({ memo }: { memo: Memo }) => {
  const renderParsedMemoContents = () => {
    // 시스템 메시지 내용에서 영문 상태를 한글로 변환
    const convertedContents = convertStatusToKorean(memo.contents);
    const parsedMemoStr = convertedContents.split("[author]");

    return parsedMemoStr.map((part, index) => {
      return index === 0 && memo.contents.startsWith("[author]") ? (
        <span className="font-semibold" key={index}>
          {memo.created_by.user_name}
        </span>
      ) : (
        <span key={index}>{part}</span>
      );
    });
  };

  return (
    <div className="flex items-center justify-between h-10 px-2 bg-gray-E rounded-s">
      <span className="text-[10px] text-gray-B font-semibold">
        시스템 메시지
      </span>
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-1 text-xs text-gray-A font-normal">
          {renderParsedMemoContents()}
        </div>
        <span className="text-[10px] text-gray-B font-semibold">
          {formatDate(memo.created_at, "MM-dd HH:mm")}
        </span>
      </div>
    </div>
  );
};
